<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm overflow-y-auto py-8" @click.self="close">
    
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl my-auto flex flex-col overflow-hidden transition-colors duration-300 mx-4" style="max-height: calc(100vh - 4rem);">
      
      <div class="px-6 py-4 border-b bg-gray-50 dark:bg-gray-700 dark:border-gray-600 flex justify-between items-center flex-shrink-0 transition-colors">
        <div class="flex items-center gap-3">
            <h2 class="text-lg font-bold text-gray-800 dark:text-white">Subir Nuevo Archivo</h2>
            <span v-if="queueLength > 0" class="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm animate-pulse">
                Quedan {{ queueLength }} en cola
            </span>
        </div>
        
        <div class="flex items-center gap-2">
            <button 
              v-if="isScannable"
              type="button"
              @click="analyzeWithGemini"
              :disabled="isAnalyzing"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold text-white transition-all shadow-sm"
              :class="isAnalyzing 
                ? 'bg-gray-400 cursor-wait' 
                : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 hover:shadow-md'"
              title="Rellenar campos automáticamente con IA"
            >
               <span v-if="isAnalyzing" class="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></span>
               <SparklesIcon v-else :size="14" />
               {{ isAnalyzing ? 'Analizando...' : 'Escanear con IA' }}
            </button>

            <button @click="close" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
              <XIcon :size="24" />
            </button>
        </div>
      </div>

      <div class="p-6 overflow-y-auto flex-1 custom-scrollbar">
        <form @submit.prevent="handleUpload" id="uploadForm">
          
          <div 
            class="p-4 rounded-lg mb-6 border transition-colors"
            :class="[
              file 
                ? 'bg-green-50 dark:bg-green-900 dark:bg-opacity-20 border-green-100 dark:border-green-800 dark:border-opacity-50'
                : 'bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 border-blue-100 dark:border-blue-800 dark:border-opacity-50'
            ]"
          >
            <div class="mb-4">
              <input type="file" ref="fileInputRef" @change="handleFileSelect" class="hidden" />
              <div 
                @click="triggerFileInput"
                @dragover.prevent="isDraggingOver = true"
                @dragleave.prevent="isDraggingOver = false"
                @drop.prevent="handleZoneDrop"
                class="block w-full border-2 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer transition-all group"
                :class="[
                  file 
                    ? 'border-green-500 bg-green-100 dark:bg-green-900 dark:bg-opacity-40'
                    : isDraggingOver 
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 dark:bg-opacity-30 border-dashed'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-green-400 border-dashed'
                ]"
              >
                 <div v-if="!file" class="text-center pointer-events-none">
                     <UploadIcon class="mx-auto h-8 w-8 text-gray-400 group-hover:text-green-500 transition-colors mb-2" />
                     <p class="text-sm text-gray-600 dark:text-gray-300 font-medium">
                       {{ isDraggingOver ? '¡Suelta el archivo aquí!' : 'Haz clic o arrastra un archivo' }}
                     </p>
                 </div>
                 <div v-else class="text-center w-full pointer-events-none flex flex-col items-center">
                    <FileIcon class="h-10 w-10 text-green-600 dark:text-green-400 mb-2" />
                    <p class="text-sm font-bold text-green-800 dark:text-green-200 break-all">{{ file.name }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-300 mt-1">{{ (file.size / 1024).toFixed(1) }} KB</p>
                    <span class="mt-2 text-xs font-bold text-green-600 bg-white dark:bg-gray-700 px-2 py-1 rounded shadow-sm border border-green-200 dark:border-green-600">
                        Clic para cambiar
                    </span>
                </div>
              </div>
            </div>

             <span class="font-semibold text-gray-700 dark:text-gray-300">Ubicación:</span>
              <div v-if="initialFolder" class="mt-1">
                <div class="flex items-center gap-1 bg-yellow-100 dark:bg-yellow-900 dark:bg-opacity-30 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded border border-yellow-200 dark:border-yellow-700">
                  <FolderIcon :size="14" /> <span>Se guardará en la carpeta seleccionada</span>
                </div>
              </div>
              <div v-else class="flex-grow flex items-center bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus-within:ring-2 focus-within:ring-green-500 transition-colors shadow-sm mt-1">
                <CalendarIcon :size="14" class="text-gray-400 mr-2 flex-shrink-0" />
                <span class="text-gray-500 dark:text-gray-400 font-medium select-none mr-0.5 bg-gray-100 dark:bg-gray-600 px-1 rounded text-xs whitespace-nowrap">
                  {{ formData.archivo_departamento }}/
                </span>
                <input type="text" v-model="formData.ruta_personalizada" class="bg-transparent border-none p-0 text-sm text-gray-700 dark:text-white placeholder-gray-400 focus:ring-0 w-full ml-1" :placeholder="`Ej: ${new Date().getFullYear()}/Facturas (opcional)`">
              </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-6">
            <div class="space-y-4">
              <div>
                <label class="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Asunto *</label>
                <input type="text" v-model="formData.asunto" required class="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-white transition-colors" placeholder="Informe mensual">
              </div>
              <div>
                <label class="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Fecha Recepción *</label>
                <DateField v-model="formData.fecha_recepcion" required :inputClass="'w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-white transition-colors'" />
              </div>
              <div>
                <label class="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Tipo Documento *</label>
                <select v-model="formData.tipo_doc" required class="w-full border rounded px-3 py-2 text-sm focus:outline-none bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-white transition-colors">
                   <option v-for="opt in optionsTipoDoc" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                </select>
              </div>
            </div>

            <div class="space-y-4">
              <div>
                <label class="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Departamento</label>
                <select v-model="formData.archivo_departamento" class="w-full border rounded px-3 py-2 text-sm focus:outline-none bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-white transition-colors">
                   <option v-for="dept in departamentos" :key="dept" :value="dept">{{ dept }}</option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Remitente</label>
                <input type="text" v-model="formData.remitente" class="w-full border rounded px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-white transition-colors" placeholder="Juan Pérez">
              </div>
              <div>
                <label class="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Origen</label>
                <select v-model="formData.origen" class="w-full border rounded px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-white transition-colors">
                   <option v-for="opt in optionsOrigen" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                </select>
              </div>
            </div>
          </div>

          <div class="border-t border-gray-200 dark:border-gray-700 my-6 relative flex items-center justify-center">
             <div class="bg-white dark:bg-gray-800 px-3 flex items-center gap-2 relative z-10 transition-colors duration-300">
                <span class="text-xs text-gray-400 font-medium uppercase tracking-wider">Campos Adicionales</span>
                
                <button 
                  type="button" 
                  @click="showFieldCreator = true" 
                  class="w-5 h-5 rounded flex items-center justify-center transition-colors bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-900 dark:bg-opacity-40 dark:text-blue-400 dark:hover:bg-blue-800"
                  title="Añadir nuevo campo"
                >
                  <PlusIcon :size="14" />
                </button>

                <button 
                  type="button" 
                  @click="toggleDeleteMode" 
                  class="w-5 h-5 rounded flex items-center justify-center transition-colors"
                  :class="deleteMode 
                    ? 'bg-red-500 text-white animate-pulse shadow-red-500/50 shadow-sm' 
                    : 'bg-gray-100 text-gray-500 hover:bg-red-100 hover:text-red-500 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-red-900/30 dark:hover:text-red-400'"
                  title="Borrar campos"
                >
                  <TrashIcon :size="14" />
                </button>
             </div>
             
             <div class="absolute inset-0 flex items-center" aria-hidden="true">
                <div class="w-full border-t border-gray-200 dark:border-gray-700"></div>
             </div>
          </div>
          
          <div v-if="showFieldCreator" class="mb-6 p-4 rounded-lg border transition-colors duration-300 bg-blue-50 border-blue-100 dark:bg-gray-700 dark:bg-opacity-50 dark:border-gray-600">
             
             <h4 class="text-xs font-bold uppercase mb-3 text-blue-600 dark:text-blue-300">Nuevo Campo Personalizado</h4>
             
             <div class="flex gap-3 items-end">
                <div class="flex-grow">
                   <label class="block text-[10px] uppercase font-bold mb-1 text-gray-500 dark:text-gray-400">Nombre del campo</label>
                   <input 
                      type="text" 
                      v-model="newField.label" 
                      placeholder="Ej: Importe Total" 
                      class="w-full text-sm border rounded px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors
                             bg-white border-gray-300 text-gray-900 
                             dark:bg-gray-900 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                   >
                </div>

                <div class="w-1/3">
                   <label class="block text-[10px] uppercase font-bold mb-1 text-gray-500 dark:text-gray-400">Tipo</label>
                   <select 
                      v-model="newField.tipo" 
                      class="w-full text-sm border rounded px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors
                             bg-white border-gray-300 text-gray-900 
                             dark:bg-gray-900 dark:border-gray-600 dark:text-white"
                   >
                      <option value="text">Texto</option>
                      <option value="number">Número</option>
                      <option value="date">Fecha</option>
                   </select>
                </div>

                <div class="flex gap-1 pb-0.5">
                   <button 
                      type="button" 
                      @click="createField" 
                      class="p-1.5 rounded transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed
                             bg-blue-600 text-white hover:bg-blue-700 
                             dark:bg-blue-600 dark:hover:bg-blue-500" 
                      :disabled="!newField.label"
                      title="Confirmar"
                   >
                      <CheckIcon :size="16" />
                   </button>
                   
                   <button 
                      type="button" 
                      @click="showFieldCreator = false" 
                      class="p-1.5 rounded transition-colors
                             bg-gray-200 text-gray-600 hover:bg-gray-300 
                             dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                      title="Cancelar"
                   >
                      <XIcon :size="16" />
                   </button>
                </div>
             </div>
          </div>

          <div v-if="fieldsLoading" class="grid grid-cols-2 gap-4 animate-pulse mb-6">
              <div v-for="i in 2" :key="i" class="h-12 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          </div>

          <div v-else-if="filteredFields.length > 0" class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-6">
              <div 
                v-for="field in filteredFields" 
                :key="field.id" 
                class="relative group transition-all duration-200 rounded-md"
                :class="{'ring-2 ring-red-500 ring-offset-2 dark:ring-offset-gray-800 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/10': deleteMode}"
                @click="handleFieldClick(field)"
              >
                   <div v-if="deleteMode" class="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                      <span class="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded shadow">BORRAR</span>
                   </div>

                   <label class="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1 truncate" :title="field.label">
                      {{ field.label }}
                   </label>
                   
                   <div :class="{'opacity-50 pointer-events-none filter blur-[1px]': deleteMode}">
                       <select v-if="field.tipo === 'select'" v-model="dynamicValues[field.key]" class="w-full border rounded px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-white">
                          <option value="">- Seleccionar -</option>
                          <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
                       </select>

                       <DateField 
                          v-else-if="field.tipo === 'date'" 
                          v-model="dynamicValues[field.key]"
                          :inputClass="'w-full border rounded px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-white'"
                       />

                       <input v-else-if="field.tipo === 'number'" type="number" step="any" v-model="dynamicValues[field.key]" class="w-full border rounded px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-white">
                       
                       <input v-else type="text" v-model="dynamicValues[field.key]" class="w-full border rounded px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-white">
                   </div>
              </div>
          </div>
          
          <div v-else-if="!showFieldCreator" class="text-center py-4 text-xs text-gray-400 italic mb-6 border border-dashed rounded dark:border-gray-700">
              No hay campos extra para este tipo de documento. Pulsa + para añadir uno.
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Notas</label>
            <textarea v-model="formData.notas" rows="3" class="w-full border rounded px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-white transition-colors placeholder-gray-400" placeholder="Información adicional sobre el archivo"></textarea>
          </div>

        </form>
      </div>

      <div class="px-6 py-4 border-t bg-gray-50 dark:bg-gray-700 dark:border-gray-600 flex justify-end gap-3 transition-colors flex-shrink-0">
        <button type="button" @click="close" class="px-4 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-500 font-medium text-sm transition-colors">
          {{ queueLength > 0 ? 'Cancelar Todo' : 'Cancelar' }}
        </button>
        <button @click="submitForm" :disabled="uploading || !file || deleteMode" class="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium text-sm shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          <span v-if="uploading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
          {{ uploading ? 'Subiendo...' : (queueLength > 0 ? 'Guardar y Siguiente' : 'Guardar Archivo') }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue';
import axios from 'axios';
import { 
    X as XIcon, Folder as FolderIcon, Calendar as CalendarIcon, 
    Upload as UploadIcon, FileText as FileIcon,
    Plus as PlusIcon, Trash as TrashIcon, Check as CheckIcon,
    Sparkles as SparklesIcon
} from 'lucide-vue-next';
import { useFields } from '../composables/useFields';
import { DOCUMENT_TYPES, DOCUMENT_ORIGINS } from '../constants/documentTypes';
import { loadDepartmentValues } from '../utils/departments';
import DateField from './DateField.vue';

const API_BASE_URL = process.env.VUE_APP_API_URL || '/api';
const props = defineProps({
    initialFolder: { type: [Number, String], default: null},
    initialFile: { type: Object, default: null },
    queueLength: { type: Number, default: 0 }
});
const emit = defineEmits(['close', 'uploaded', 'add-to-queue']);

// Composable de campos. Usamos createField/deleteField del composable
// (que ya usa axios y se beneficia del refresh JWT) en lugar de hacer
// axios.post/delete manuales y duplicar la lógica.
const { fields, loading: fieldsLoading, fetchFields, createField: createFieldApi, deleteField: deleteFieldApi } = useFields();

// Estado Reactivo Base
const dynamicValues = reactive({}); 
const fileInputRef = ref(null);
const file = ref(null);
const uploading = ref(false);
const isDraggingOver = ref(false);
const savedFormState = ref(null);

// Estado Análisis IA
const isAnalyzing = ref(false);

// --- ESTADO PARA GESTIÓN DE CAMPOS DINÁMICOS ---
const showFieldCreator = ref(false);
const deleteMode = ref(false);
const newField = reactive({
    label: '',
    tipo: 'text'
});

// --- COMPUTED: ¿Es archivo escaneable? ---
const isScannable = computed(() => {
    if (!file.value) return false;
    const type = file.value.type;
    return type === 'application/pdf' || type.startsWith('image/');
});

// --- LÓGICA DE ANÁLISIS CON IA ---
const analyzeWithGemini = async () => {
    if (!file.value) return;

    isAnalyzing.value = true;
    try {
        const formDataPayload = new FormData();
        formDataPayload.append('file', file.value);

        // El interceptor de axios añade el Authorization. No hace falta
        // construir headers manualmente.
        const response = await axios.post(
            `${API_BASE_URL}/analyze-document/`,
            formDataPayload,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        const aiData = response.data; // Esperamos { core: {}, extra: [] }

        // 1. Rellenar Campos Core
        if (aiData.core) {
            if (aiData.core.asunto) formData.asunto = aiData.core.asunto;
            if (aiData.core.fecha_recepcion) formData.fecha_recepcion = aiData.core.fecha_recepcion;
            if (aiData.core.remitente) formData.remitente = aiData.core.remitente;
            // Solo asignar tipo_doc si es válido en nuestra lista para evitar valores raros
            if (aiData.core.tipo_doc && optionsTipoDoc.some(o => o.value === aiData.core.tipo_doc)) {
                formData.tipo_doc = aiData.core.tipo_doc;
            }
        }

        // 2. Procesar Campos Extra (Creación Dinámica)
        if (aiData.extra && Array.isArray(aiData.extra)) {
            for (const item of aiData.extra) {
                const keyToUse = slugify(item.label);
                const exists = fields.value.find(f => f.key === keyToUse);

                if (!exists) {
                    // La IA sugiere un campo nuevo: lo creamos en BD
                    const lastOrder = fields.value.length > 0
                        ? Math.max(...fields.value.map(f => f.orden))
                        : 0;
                    const created = await createFieldApi({
                        key: keyToUse,
                        label: item.label,
                        tipo: item.tipo || 'text',
                        orden: lastOrder + 1,
                        activo: true,
                        categoria: formData.tipo_doc
                    });
                    if (!created) {
                        console.warn(`No se pudo crear el campo automático ${item.label}`);
                        continue;
                    }
                }

                // Asignamos el valor
                dynamicValues[keyToUse] = item.value;
            }

            // createFieldApi ya hace fetchFields internamente, pero por
            // claridad lo dejamos explícito si no se creó ninguno nuevo.
            await fetchFields();
        }
    } catch (error) {
        console.error("Error analizando documento:", error);
        let msg = "No se pudo analizar el documento.";
        if (error.response && error.response.data && error.response.data.error) {
            msg += ` ${error.response.data.error}`;
        }
        alert(msg);
    } finally {
        isAnalyzing.value = false;
    }
};

// --- LÓGICA DE GESTIÓN DE CAMPOS ---

const toggleDeleteMode = () => {
    deleteMode.value = !deleteMode.value;
    if (deleteMode.value) showFieldCreator.value = false;
};

const slugify = (text) => {
    return text.toString().toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "") 
        .replace(/[^a-z0-9\s-_]/g, '') 
        .trim()
        .replace(/\s+/g, '_');
};

const createField = async () => {
    if (!newField.label) return;

    const generatedKey = slugify(newField.label);

    if (fields.value.some(f => f.key === generatedKey)) {
        alert(`Ya existe un campo visible con la clave interna "${generatedKey}".`);
        return;
    }

    const lastOrder = fields.value.length > 0
        ? Math.max(...fields.value.map(f => f.orden))
        : 0;

    const created = await createFieldApi({
        key: generatedKey,
        label: newField.label,
        tipo: newField.tipo,
        orden: lastOrder + 1,
        activo: true,
        categoria: formData.tipo_doc
    });

    if (created) {
        newField.label = '';
        newField.tipo = 'text';
        showFieldCreator.value = false;
    } else {
        alert("Error al crear el campo.");
    }
};

const handleFieldClick = async (field) => {
    if (!deleteMode.value) return;

    if (confirm(`¿Estás seguro de que quieres eliminar permanentemente el campo "${field.label}"?`)) {
        const deleted = await deleteFieldApi(field.id);
        if (!deleted) alert("Error al borrar el campo.");
    }
};

// --- NUEVO: FILTRADO DE CAMPOS ---
const filteredFields = computed(() => {
    if (!formData.tipo_doc) return fields.value.filter(field => !field.categoria);
    return fields.value.filter(field => !field.categoria || field.categoria === formData.tipo_doc);
});

// --- RESTO DEL CÓDIGO DEL FORMULARIO (Igual que antes) ---

const formData = reactive({
  asunto: '',
  fecha_recepcion: new Date().toISOString().slice(0, 10),
  origen: 'Correo electronico',
  archivo_departamento: 'General',
  tipo_doc: 'Factura',
  remitente: '',
  notas: '',
  // Vacío por defecto. El usuario rellena la ruta solo si quiere agrupar
  // dentro del departamento (ej: "2026/Facturas"). Antes venía con el año
  // actual y creaba subcarpetas anuales automáticamente, comportamiento
  // sorpresivo para quien no lo esperaba.
  ruta_personalizada: ''
});

const optionsOrigen = DOCUMENT_ORIGINS;
const optionsTipoDoc = DOCUMENT_TYPES;
const departamentos = ref(['General', 'Ventas', 'Compras', 'Gestoria']);

// Watcher cola
watch(() => props.initialFile, (newFile) => {
    if (newFile) {
        file.value = newFile;
        formData.asunto = newFile.name.split('.')[0].replace(/[-_]/g, ' ');
        if (savedFormState.value) {
            formData.fecha_recepcion = savedFormState.value.fecha_recepcion;
            formData.origen = savedFormState.value.origen;
            formData.archivo_departamento = savedFormState.value.archivo_departamento;
            formData.tipo_doc = savedFormState.value.tipo_doc;
            formData.remitente = savedFormState.value.remitente;
            formData.notas = savedFormState.value.notas;
            formData.ruta_personalizada = savedFormState.value.ruta_personalizada;
            if (savedFormState.value.dynamicValues) {
                Object.keys(savedFormState.value.dynamicValues).forEach(key => {
                    dynamicValues[key] = savedFormState.value.dynamicValues[key];
                });
            }
        }
    }
}, { immediate: true });

onMounted(async () => {
    departamentos.value = loadDepartmentValues();
    await fetchFields();
});

const triggerFileInput = () => { fileInputRef.value.click(); };

const handleFileSelect = (event) => {
  const selected = event.target.files[0];
  if (selected) {
    file.value = selected;
    if (!formData.asunto) {
        formData.asunto = selected.name.split('.')[0].replace(/[-_]/g, ' ');
    }
  }
};

const handleZoneDrop = (e) => {
  isDraggingOver.value = false;
  const droppedFiles = Array.from(e.dataTransfer.files);
  if (droppedFiles.length > 0) {
    file.value = droppedFiles[0];
    if (!formData.asunto) {
      formData.asunto = file.value.name.split('.')[0].replace(/[-_]/g, ' ');
    }
    if (droppedFiles.length > 1) {
      emit('add-to-queue', droppedFiles.slice(1));
    }
  }
};

const submitForm = () => {
    if (file.value && formData.asunto) handleUpload();
    else alert("Por favor, selecciona un archivo y escribe un asunto.");
};

const handleUpload = async () => {
  if (!file.value) return;
  uploading.value = true;
  try {
    const data = new FormData();

    // 1. Campos core: aplanados como atributos individuales.
    //    NOTA: omitimos 'ruta_personalizada' del payload core porque el
    //    backend la mapea a 'custom_path' aparte (ver más abajo).
    Object.keys(formData).forEach(key => {
      if (key !== 'ruta_personalizada') {
        data.append(key, formData[key]);
      }
    });

    // 2. Archivo binario
    data.append('enlace_digital_upload', file.value);

    // 3. Campos dinámicos: el backend (to_internal_value) detecta automáticamente
    //    cualquier campo no nativo del modelo y lo guarda en datos_extra.
    //    Mandar 'datos_extra' como JSON string era frágil porque DRF dependía
    //    de aceptarlo como JSONField; aplanar es consistente con el aplanado
    //    que el serializer hace en la respuesta.
    filteredFields.value.forEach(f => {
      const v = dynamicValues[f.key];
      if (v !== undefined && v !== null && v !== '') {
        data.append(f.key, v);
      }
    });

    // 4. Carpeta / ruta personalizada.
    //    Si hay initialFolder, se sube a esa carpeta directamente.
    //    Si NO hay initialFolder, se usa custom_path para crear subcarpetas
    //    dentro del departamento.
    if (props.initialFolder) {
      data.append('folder', props.initialFolder);
    } else if (formData.ruta_personalizada) {
      data.append('custom_path', formData.ruta_personalizada);
    }

    // El interceptor de axios ya añade el header Authorization
    await axios.post(`${API_BASE_URL}/files/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    // Persistir estado del formulario para que las subidas en cola reusen
    // los mismos valores (departamento, fechas, etc.)
    const stateToSave = {
      fecha_recepcion: formData.fecha_recepcion,
      origen: formData.origen,
      archivo_departamento: formData.archivo_departamento,
      tipo_doc: formData.tipo_doc,
      remitente: formData.remitente,
      notas: formData.notas,
      ruta_personalizada: formData.ruta_personalizada,
      dynamicValues: { ...dynamicValues }
    };
    savedFormState.value = stateToSave;

    file.value = null;
    formData.asunto = '';

    emit('uploaded');

  } catch (error) {
    let msg = "Error desconocido";
    if (error.response && error.response.data) {
        msg = JSON.stringify(error.response.data);
    }
    alert("Error: " + msg);
  } finally {
    uploading.value = false;
  }
};

const close = () => {
  emit('close');
  file.value = null;
  formData.asunto = '';
  Object.keys(dynamicValues).forEach(key => delete dynamicValues[key]);
  savedFormState.value = null;
  deleteMode.value = false;
  showFieldCreator.value = false;
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(156, 163, 175, 0.5); border-radius: 20px; }
</style>